import { useState } from "react";
import { Mail, MessageCircle, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "General enquiry", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    if (!form.name.trim() || !form.email.includes("@") || form.message.trim().length < 10) {
      toast.error("Please fill in your name, a valid email and a message (10+ characters)");
      return;
    }
    setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send your message");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-6 py-12 md:px-10" data-testid="contact-page">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Contact</p>
      <h1 className="mt-3 font-display text-4xl font-medium tracking-tighter sm:text-5xl">We're here to help.</h1>
      <p className="mt-4 max-w-xl text-muted-foreground">Questions, feedback or partnership ideas — we reply within 24 hours.</p>

      <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-4">
          <a href="mailto:support@zuntria.co.uk" className="flex items-center gap-3 rounded-2xl border border-border bg-card p-5 transition-colors hover:border-primary/50" data-testid="contact-email-support">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary"><Mail className="h-4 w-4" /></span>
            <div>
              <div className="text-sm font-medium">Support</div>
              <div className="text-xs text-muted-foreground">support@zuntria.co.uk</div>
            </div>
          </a>
          <a href="mailto:hello@zuntria.co.uk" className="flex items-center gap-3 rounded-2xl border border-border bg-card p-5 transition-colors hover:border-primary/50" data-testid="contact-email-hello">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent/10 text-accent"><Mail className="h-4 w-4" /></span>
            <div>
              <div className="text-sm font-medium">Partnerships & press</div>
              <div className="text-xs text-muted-foreground">hello@zuntria.co.uk</div>
            </div>
          </a>
          <a href="https://wa.me/447587831674" target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-2xl border border-border bg-card p-5 transition-colors hover:border-primary/50" data-testid="contact-whatsapp">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-500"><MessageCircle className="h-4 w-4" /></span>
            <div>
              <div className="text-sm font-medium">WhatsApp</div>
              <div className="text-xs text-muted-foreground">+44 7587 831674</div>
            </div>
          </a>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 md:p-8 lg:col-span-2">
          {sent ? (
            <div className="flex flex-col items-center py-10 text-center" data-testid="contact-success">
              <CheckCircle2 className="h-10 w-10 text-emerald-500" />
              <h2 className="mt-4 font-display text-xl">Message sent!</h2>
              <p className="mt-2 text-sm text-muted-foreground">We'll get back to you within 24 hours.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label>Your name</Label>
                  <Input className="mt-1.5" value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} data-testid="contact-name-input" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" className="mt-1.5" value={form.email} onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))} data-testid="contact-email-input" />
                </div>
              </div>
              <div>
                <Label>Subject</Label>
                <Input className="mt-1.5" value={form.subject} onChange={(e) => setForm((s) => ({ ...s, subject: e.target.value }))} data-testid="contact-subject-input" />
              </div>
              <div>
                <Label>Message</Label>
                <Textarea rows={5} className="mt-1.5" value={form.message} onChange={(e) => setForm((s) => ({ ...s, message: e.target.value }))} placeholder="How can we help?" data-testid="contact-message-input" />
              </div>
              <Button onClick={submit} disabled={busy} className="w-full sm:w-auto" data-testid="contact-submit-btn">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Send message
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
